import React, { useEffect, useRef, useState } from "react";
import "./Tabs.scss";

export type TabOption<V extends string = string> = {
  value: V;
  label: React.ReactNode;
  /** A number after the label */
  count?: number;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type TabsProps<V extends string> = {
  options: TabOption<V>[];
  value: V;
  onChange: (value: V) => void;
  ariaLabel: string;
  className?: string;
};

// Arrow keys move between tabs (the WAI-ARIA tabs pattern, activation follows focus).
function useArrowKeys<V extends string>(options: TabOption<V>[], onChange: (v: V) => void) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    const at = enabled.indexOf(index);
    let next: number | undefined;
    if (e.key === "ArrowRight") next = enabled[(at + 1) % enabled.length];
    else if (e.key === "ArrowLeft") next = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    refs.current[next]?.focus();
    onChange(options[next].value);
  };
  return { refs, onKeyDown };
}

// On a narrow screen the strip scrolls: keep the active tab in view and fade the edge that has more tabs behind it.
function useOverflow<V extends string>(value: V, refs: React.MutableRefObject<(HTMLButtonElement | null)[]>, options: TabOption<V>[]) {
  const strip = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const measure = () =>
      setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);
  useEffect(() => {
    const index = options.findIndex((o) => o.value === value);
    const tab = refs.current[index];
    const el = strip.current;
    if (!tab || !el || el.scrollWidth <= el.clientWidth) return;
    const left = tab.offsetLeft - (el.clientWidth - tab.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [value, options, refs]);
  return { strip, edges };
}

function renderTabs<V extends string>(
  { options, value, onChange, ariaLabel, className }: TabsProps<V>,
  base: string,
  refs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
  onKeyDown: (index: number) => (e: React.KeyboardEvent) => void,
  overflow?: { strip: React.RefObject<HTMLDivElement>; edges: { left: boolean; right: boolean } }
) {
  return (
    <div
      ref={overflow?.strip}
      className={[base, overflow?.edges.left ? "fade-left" : "", overflow?.edges.right ? "fade-right" : "", className]
        .filter(Boolean)
        .join(" ")}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((option, index) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => (refs.current[index] = el)}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            disabled={option.disabled}
            className={`${base}__tab${on ? ` ${base}__tab--on` : ""}`}
            onClick={() => onChange(option.value)}
            onKeyDown={onKeyDown(index)}
          >
            {option.icon ? <span className={`${base}__icon`}>{option.icon}</span> : null}
            {option.label}
            {option.count !== undefined ? <span className={`${base}__count`}>{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** A tab strip with the slanted orange underline on the active tab: a page's sections. */
export function Tabs<V extends string = string>(props: TabsProps<V>): React.JSX.Element {
  const { refs, onKeyDown } = useArrowKeys(props.options, props.onChange);
  const overflow = useOverflow(props.value, refs, props.options);
  return renderTabs(props, "ui-tabs", refs, onKeyDown, overflow);
}

/** A compact group of slanted segments, the active one orange: a filter or a view switch (List · Grid). */
export function Segmented<V extends string = string>(props: TabsProps<V>): React.JSX.Element {
  const { refs, onKeyDown } = useArrowKeys(props.options, props.onChange);
  return renderTabs(props, "ui-seg", refs, onKeyDown);
}

export default Tabs;
